import { ImageLoader } from 'next/image'
import router from 'next/router'
import { UserInfoFromApi, UserTotalFunds } from '../context'
import { get } from './axios'
import {
  HeaderMenuType,
  RiseColorEnum,
  HeaderSettingData,
  HeaderCheckData,
  TextLink,
  LocalesDataMap,
  LocalesDataEnum,
} from '../types'
import {
  SUB_WEBSITE,
  GET_LOGIN_USER,
  GET_USER_TIER_INFO,
  GET_SITE_SETTINGS,
  GET_COMPLIANCE_CHECK,
  GET_TOTAL_FUNDS,
} from './const'
import { getTodayFormatString } from '@gateio/core-utils'
import { IncomingMessage, ServerResponse } from 'http'
import { setCookie } from 'cookies-next'

export const getUserInfo = async (): Promise<UserInfoFromApi | null> => {
  try {
    const userInfoResponse = await get<UserInfoFromApi>({
      isInternalReq: false,
      url: GET_USER_TIER_INFO,
    })
    if (userInfoResponse?.code !== 0) {
      return null
    }
    const userInfo = userInfoResponse?.data
    return typeof userInfo === 'string' ? JSON.parse(userInfo) : userInfo
  } catch (err) {
    return null
  }
}

export const getLoginUserInfo = async (): Promise<{
  country: number
  verified: number
} | null> => {
  try {
    const userInfoResponse = await get<{
      country: number
      verified: number
    }>({
      isInternalReq: false,
      url: GET_LOGIN_USER,
    })
    if (userInfoResponse?.code !== 0) {
      return null
    }
    const userInfo = userInfoResponse?.data
    return userInfo
  } catch (err) {
    return null
  }
}

export const getHeaderCommonData = async (): Promise<HeaderSettingData> => {
  try {
    const res = await get<HeaderSettingData>({
      url: GET_SITE_SETTINGS,
    })
    return res.data
  } catch (err) {
    return {
      lang_list: {},
      fiat_list: {},
      is_service_host: false,
      curr_fiat: 'USD',
      currency_count: '2700',
      curr_fiat_market_rates: {},
      defaultBuyCryptoFiat: 'USD',
      defaultP2PFiat: 'USD',
    }
  }
}

// https://app.apifox.com/project/2898814/apis/api-153426685
export const getComplianceCheck = async (): Promise<HeaderCheckData> => {
  try {
    const menuData = await get<HeaderCheckData>({
      url: GET_COMPLIANCE_CHECK,
    })
    return menuData?.data
  } catch (err) {
    return {
      business: [],
      limit: {},
      toast: {},
      menu: [],
    }
  }
}

/**
 * 获取用户总资产
 * @returns 用户总资产{ total_btc: string, total_usd: string }
 */
export const getTotalFunds = async (): Promise<UserTotalFunds> => {
  try {
    const totalFundsData = await get<UserTotalFunds>({
      isInternalReq: false,
      url: GET_TOTAL_FUNDS,
    })
    return totalFundsData?.data || { total_btc: '0', total_usd: '0' }
  } catch (err) {
    return { total_btc: '0', total_usd: '0' }
  }
}

export function isClient(): boolean {
  return typeof window !== 'undefined'
}

export const coinImgLoader: ImageLoader = ({ src, width }) => {
  const isParJoin = src.includes('?') ? '&' : '?'
  if (src?.startsWith('http')) return `${src}${isParJoin}w=${width || 28}`
  if (process.env.NODE_ENV === 'development') {
    return `https://www.gate.io${src}${isParJoin}w=${width || 28}`
  }
  return `${src}${isParJoin}w=${width || 28}`
}

/**
 * 根据多级嵌套的children获取hidden
 * @param menuData HeaderMenuType[]
 * @param pathname string 可以是name，也可以是name.subname，或者name.subname.subname
 * @param isSecondNavHidden boolean 是否判断二级导航要不要隐藏
 * @returns boolean
 */
export const getHiddenFromMultilArray = ({
  flatMenuData,
  nestedMenuData = [],
  multiplePathName,
  isSecondNavHidden,
}: {
  flatMenuData?: string[]
  nestedMenuData?: HeaderMenuType[]
  multiplePathName: string
  isSecondNavHidden?: boolean
  isClientLoading?: boolean
}) => {
  if (!multiplePathName) return true
  const pathnames = multiplePathName.split('.') || []
  if (flatMenuData) {
    const lastPathname = pathnames[pathnames.length - 1]
    // 如果在加载中 默认给个状态
    // if (isClientLoading) {
    //   return true
    // }
    return flatMenuData.includes(lastPathname)
  }
  let currentMenuAry = Array.from(nestedMenuData)
  let currentMenuData: HeaderMenuType = {
    code: '',
    hidden: true,
    child: [],
  }
  for (let i = 0; i < pathnames.length; i++) {
    const name = pathnames[i]
    const item = currentMenuAry.find(menu => menu.code === name)
    if (item) {
      currentMenuAry = item.child || []
      currentMenuData = item
    } else {
      return true
    }
  }
  // 可以通过子项的hidden来控制父项的hidden
  if (isSecondNavHidden) {
    if (currentMenuData.hidden) {
      return true
    } else {
      // 如果当前项不隐藏，但是子项全部隐藏，那么当前项也隐藏
      return currentMenuData.child?.length ? currentMenuData.child.every((item: HeaderMenuType) => item.hidden) : true
    }
  }

  return currentMenuData.hidden
}

// 如果cookie中某个key存在非英文，导致node报错，所以针对所有字段进行encodeURI
export const encodeValueFromCookie = (cookieStr: string, locale?: string) => {
  const cookieStrArr = cookieStr.split(';')
  let newCookieStr = ''
  let hasLangCookie = false

  if (cookieStrArr.length) {
    cookieStrArr.forEach((item: string) => {
      if (item.trim()) {
        const key = item.split('=')[0].trim()
        const keyValue = item.split('=')[1]

        // 如果是lang且locale存在，使用新的locale值
        if (key === 'lang' && locale) {
          hasLangCookie = true
          newCookieStr += `${key}=${LocalesDataMap[locale as LocalesDataEnum]};`
        } else if (key && keyValue) {
          newCookieStr += `${key}=${encodeURI(keyValue)};`
        } else {
          newCookieStr += `${item};`
        }
      }
    })
  }

  // 如果locale存在且没有找到lang cookie，添加新的lang cookie
  if (locale && !hasLangCookie) {
    newCookieStr += `lang=${encodeURI(LocalesDataMap[locale as LocalesDataEnum])};`
  }

  return newCookieStr
}

// VIP等级小于11，且为非企业用户
export const isStateLimitPage = (isLimit: boolean, tier_level: number | string, verified: string) => {
  return isLimit && Number(tier_level ?? 0) <= 10 && verified && verified !== '3'
}

// 根据语言设置默认涨跌颜色
export const getDefaultRiseColor = (locale?: string) => {
  return locale === 'zh' ? RiseColorEnum.risered : RiseColorEnum.risegreen
}

/**
 * px转换为rem
 * @param pxValue 输入的设计稿中的px值
 * @returns 返回转换后的rem值
 */
export const pxToRem = (pxValue: TemplateStringsArray, ...values: string[]) => {
  const pxInt: string = pxValue[0] ? pxValue[0] : values[0]
  try {
    const px = parseFloat(pxInt)
    return px + 'rem'
  } catch (e) {
    return pxInt + 'px'
  }
}

/**
 * 添加h5模式768设计稿下的值
 * @param pxValue 输入的设计稿中的px值
 * @returns 返回media 768设计稿下的转换为rem值
 */
export const pxToRemMobile = (pxValue: TemplateStringsArray) => {
  const media = `@media (max-width:768px) and (orientation:portrait)`
  const values = pxValue[0]
  try {
    return `${media}{${values.replaceAll('px', 'rem')}}`
  } catch (e) {
    return values
  }
}

export const transLangList = (langList: Record<string, string>): any => {
  const newLangList: any = {}
  if (!langList) {
    return {}
  }
  Object.keys(langList).forEach((langKey: string) => {
    switch (langKey) {
      case 'vn':
        newLangList['vi'] = langList[langKey]
        break
      case 'cn':
        newLangList['zh'] = langList[langKey]
        break
      case 'tw':
        newLangList['zh-tw'] = langList[langKey]
        break
      case 'br':
        newLangList['pt-br'] = langList[langKey]
        break
      default:
        newLangList[langKey] = langList[langKey]
        break
    }
  })
  return newLangList
}

export enum KYCLevels {
  KYC_LV_0 = 0, // 'KYC 0 等级'
  KYC_LV_0_5 = 5, // 'KYC 0.5 等级，介于 KYC 0 和 KYC 1 之间的等级，需满足完成 LN 扫描'
  KYC_LV_1 = 2, // 'KYC 1 等级'
  KYC_LV_2 = 1, // 'KYC 2 等级'
  KYC_LV_3 = 4, // 'KYC 3 等级'
  KYC_LV_COMPANY = 3, // 'KYC 企业认证'
}

export const isUnCompletedKYC2 = (verified: number) => {
  return ![KYCLevels.KYC_LV_2, KYCLevels.KYC_LV_3, KYCLevels.KYC_LV_COMPANY].includes(verified)
}

export const getLangPrefix = () => {
  const langPrefix = router.locale === 'en' ? '' : `/${router.locale}`
  return langPrefix
}

export const getUrlSearch = (search: string) => {
  const temp = new URLSearchParams(window.location.href.split('?')[1])
  const value = temp.get(search)
  return value ? value.split('#')[0] : ''
}

/**
 * 加载image的loader
 * @param src 引入路劲
 * @param width 图片宽度
 * @param quality 图片质量，默认75
 * @returns 返回loader组装的image链接地址
 */
export const imgLoader: ImageLoader = ({ src, width, quality }) => {
  return `${src}?w=${width}&q=${quality || 75}`
}

/**
 * 获取当前seo相关标准化的信息，其他信息属于业务定制化的
 * @param currLocale 当前语言
 * @param supportLocales 支持的语言
 * @param currAsPath 当前路由
 * @param subWebsiteId 子站点id
 * @returns canonical、languageAlternates
 */
export const getStandardizedSeoConfig = ({
  currLocale,
  supportLocales,
  currAsPath,
  subWebsiteId,
  referer,
}: {
  currLocale?: string
  supportLocales?: string[]
  currAsPath: string
  subWebsiteId: number
  referer: string
}) => {
  const simpleAsPath = currAsPath === '/' ? '' : currAsPath.split('?')[0].split('#')[0]
  const defaultLang = getDefaultLang(subWebsiteId)
  /* 设置权威url */
  const canonicalUrl =
    currLocale === defaultLang ? `${referer}${simpleAsPath}` : `${referer}/${currLocale}${simpleAsPath}`
  const languageAlternates = (supportLocales || []).map(item => ({
    hrefLang: item,
    href: item === defaultLang ? `${referer}${simpleAsPath}` : `${referer}/${item}${simpleAsPath}`,
  }))
  /* 设置备用url */
  const defaultLanguageAlternate =
    subWebsiteId === SUB_WEBSITE.SUB_WEBSITE_ID_MAIN
      ? [
          { hrefLang: 'x-default', href: `${referer}${simpleAsPath}` },
          { hrefLang: 'en-tr', href: `${referer}/tr${simpleAsPath}` },
        ]
      : [{ hrefLang: 'x-default', href: `${referer}${simpleAsPath}` }]
  const languageAlternateList = languageAlternates.concat(defaultLanguageAlternate)

  return {
    canonicalUrl,
    languageAlternateList,
  }
}

/**
 * 获取默认语言
 * @param subWebsiteId 站点id
 * @returns 默认语言
 */
export const getDefaultLang = (subWebsiteId: number) => {
  return subWebsiteId === SUB_WEBSITE.SUB_WEBSITE_ID_TURKEY ? 'tr' : 'en'
}

/**
 * 币种图标增加版本号
 * @param coinName 币种名称
 * @param coinIconVersionWhiteList 币种缓存白名单
 * @returns 字符
 */
export const coinAddMarketVersion = (coinName = '', coinIconVersionWhiteList: string[]) => {
  const dateNowTimestamp = getTodayFormatString()

  if (coinIconVersionWhiteList.includes(coinName?.toLowerCase())) {
    return `?v=${dateNowTimestamp}`
  }
  return ''
}

/**
 * SSR 服务端页面语言重定向逻辑
 * @param req req对象
 * @param res res对象
 * @param locale 当前语言
 * @param commonData ssr请求的commondata数据
 * @param resolvedUrl 当前url
 * @returns 重定向的url
 */
export const serverRedirectLang = ({
  req,
  res,
  locale,
  commonData,
  resolvedUrl,
}: {
  req: IncomingMessage
  res: ServerResponse<IncomingMessage>
  locale: string
  commonData: any
  resolvedUrl: string
}) => {
  const { lang_list = {}, sub_website_id = 0 } = commonData
  const whiteAcceptLangs = sub_website_id === SUB_WEBSITE.SUB_WEBSITE_ID_MAIN ? ['tr'] : []
  const langList = Object.keys(lang_list) || []
  const acceptLangs = Array.from(new Set(langList.concat(whiteAcceptLangs)))

  if (langList.length) {
    if (!acceptLangs.includes(locale)) {
      setCookie('lang', getDefaultLang(sub_website_id), { req, res })
      return resolvedUrl
    }
  }
  return null
}

/**
 * 将content字符串中的关键词替换为对应的超链接
 * @param content - 原始字符串
 * @param textLink - 包含关键词和链接的配置数组
 * @returns 处理后的字符串，带有超链接
 */
export const linkifyContent = (content: string, textLink: TextLink[]): string => {
  let newContent = content
  // 遍历textLink数组，将每个关键词替换为对应的<a>标签
  textLink?.forEach(({ key, link }) => {
    const regex = new RegExp(key, 'g')
    newContent = newContent.replace(regex, `<a href="${link}" target="__blank">${key}</a>`)
  })

  return newContent
}

// 添加埋点
export function fnAddPoint(clickName: string, params = {}) {
  if (window.collectEvent) {
    const _params = typeof params == 'object' ? params : { button_name: params }
    window.collectEvent('beconEvent', clickName, _params)
  }
}
/**
 * 如果是tr语言，需要特殊处理
 * @param locale 当前语言
 */
export const checkTrLang = (locale = 'en', targetLocale = 'en') => {
  const sub_website_id = process.env.SUB_WEBSITE_ID ? Number(process.env.SUB_WEBSITE_ID) : 0
  const isMainWebSite = sub_website_id === SUB_WEBSITE.SUB_WEBSITE_ID_MAIN
  const isTurkeyWebSite = sub_website_id === SUB_WEBSITE.SUB_WEBSITE_ID_TURKEY
  let redirectLocale = ''
  if (locale === 'tr' && isMainWebSite) {
    redirectLocale = targetLocale
  }
  if (locale === 'tr' && isTurkeyWebSite) {
    redirectLocale = 'tr'
  }
  return redirectLocale
}

/**
 * 其他扩展语言包方法
 * @param locale
 */
export const getExtralTranslationLang = (locale: string) => {
  const localesMap = {
    [LocalesDataEnum['en-eu']]: 'en',
    [LocalesDataEnum['it-eu']]: 'it',
  }
  return localesMap[locale as keyof typeof localesMap]
}
/**
 * 判断小币种后面是否跟着连续0，0.0000000689 -> 0.0{6}89  大于等于7个0，进行缩写
 * @param {string} str
 *
 */
export function formatContinuousDecimal(str: string, precision?: number | string) {
  if (!str) return str
  try {
    const strValue = String(str)
    const formatFun = (value: string) => {
      const part = value.split('.')
      if (part.length < 2) {
        return value
      } else {
        const formatDecimalPart = part[1].replace(/(0+)(?=[1-9])/g, match => {
          return match.length >= 7 ? `0{${match.length}}` : match
        })
        return `${part[0]}.${formatDecimalPart}`
      }
    }
    if (precision) {
      const precision_value = parseFloat(strValue).toFixed(Number(precision))
      return formatFun(`${precision_value}`)
    }
    return formatFun(strValue)
  } catch (error) {
    return str
  }
}

/**
 * 整体将原有文案中的mt分站特殊文案替换成eu分站的，无需重提翻译
 *  Gate.MT --> Gate.io EU
 *  support@gate.mt --> support_eu@gate.io
 *  info@gate.mt --> info_eu@gate.io
 *  institutional@gate.mt --> institutional_eu@gate.io
 */
export const replaceMtSpecialText = (text: string) => {
  const replaceMap = {
    'Gate.MT': 'Gate.io EU',
    'support@gate.mt': 'support_eu@gate.io',
    'info@gate.mt': 'info_eu@gate.io',
    'institutional@gate.mt': 'institutional_eu@gate.io',
  }
  return text.replace(
    /(Gate\.MT|support@gate\.mt|info@gate\.mt|institutional@gate\.mt)/g,
    match => replaceMap[match as keyof typeof replaceMap]
  )
}
